/** @jsxImportSource @emotion/react */
import { ReactNode } from 'react'

import { formatCalendarDate } from 'helpers/dates'
import { BillingEraPaymentSummary } from 'store/types'

// Every file header uses these same columns, none sized by its content, so
// payers, amounts, dates and trace numbers line up down the page. An empty
// column takes the spare width, keeping the details together and the actions
// at the right edge:
// chevron | file | payer | method | amount | check date | claims | trace | (spare) | actions
export const ERA_FILE_HEADER_COLUMNS =
  '16px minmax(190px, 320px) minmax(110px, 300px) 96px 96px 84px 48px minmax(100px, 220px) minmax(0, 1fr) 192px'

const METHODS: Record<string, { label: string; title: string; color: string; background: string }> = {
  ACH: { label: 'ACH', title: 'Paid by ACH transfer', color: '#1e7e34', background: '#e3f4e8' },
  // Financial institution option: an electronic transfer whose rail the payer's bank picks.
  BOP: { label: 'ACH', title: 'Electronic transfer, sent as the payer\'s bank chose (BOP)', color: '#1e7e34', background: '#e3f4e8' },
  FWT: { label: 'Wire', title: 'Paid by wire transfer (FWT)', color: '#1e7e34', background: '#e3f4e8' },
  CHK: { label: 'Check', title: 'Paid by check', color: '#0b5ed7', background: '#e7f0ff' },
  NON: { label: 'No payment', title: 'Remittance without a payment (NON)', color: '#6c757d', background: '#eceff1' },
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

interface CellProps {
  label: string
  column: number
  row: number
  align?: 'left' | 'right'
  // The full value, for a hover when it is cut short.
  title?: string
  children: ReactNode
}

// A caption over its value; a value too long for the column ends in an ellipsis.
export const EraHeaderCell = ({ label, column, row, align = 'left', title, children }: CellProps) => (
  <div css={{ gridColumn: column, gridRow: row, minWidth: 0, textAlign: align }}>
    <div
      className="text-muted"
      css={{ fontSize: 11, lineHeight: '14px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {label}
    </div>
    <div
      title={title}
      css={{
        fontSize: 14,
        lineHeight: '20px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontVariantNumeric: 'tabular-nums',
      }}>
      {children}
    </div>
  </div>
)

interface PaymentCellsProps {
  payment: BillingEraPaymentSummary
  // The grid row this payment takes; a file with several payments takes one each.
  row: number
  // Shown when the file holds several payments.
  number?: number
}

// One payment's columns on a file header.
export const EraPaymentCells = ({ payment, row, number }: PaymentCellsProps) => {
  const payer = payment.payerName || 'Unknown Payer'
  const method = METHODS[payment.paymentMethod] ?? {
    label: payment.paymentMethod || '-',
    title: payment.paymentMethod || '',
    color: '#6c757d',
    background: '#eceff1',
  }
  const amount = Number(payment.checkAmount) || 0
  const trace = payment.checkNumber || '-'

  return (
    <>
      <EraHeaderCell label="Payer" column={3} row={row} title={payer}>
        {number !== undefined && <span className="text-muted mr-1">#{number}</span>}
        <span css={{ fontWeight: 600 }}>{payer}</span>
      </EraHeaderCell>
      <EraHeaderCell label="Method" column={4} row={row} title={method.title}>
        <span
          css={{
            display: 'inline-block',
            padding: '0 8px',
            borderRadius: 10,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: '20px',
            color: method.color,
            background: method.background,
          }}>
          {method.label}
        </span>
      </EraHeaderCell>
      <EraHeaderCell label="Amount" column={5} row={row} align="right">
        <span className={amount ? undefined : 'text-muted'}>{usd.format(amount)}</span>
      </EraHeaderCell>
      <EraHeaderCell label="Check Date" column={6} row={row}>
        {formatCalendarDate(payment.checkDate)}
      </EraHeaderCell>
      <EraHeaderCell label="Claims" column={7} row={row} align="right">
        {payment.claimsAmount ?? '-'}
      </EraHeaderCell>
      <EraHeaderCell label="Check / Trace #" column={8} row={row} title={trace}>
        {trace}
      </EraHeaderCell>
    </>
  )
}
